import React, { useState, useMemo } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";
import { SearchIcon, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from '@/components/ui/toast';
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { calculateUnits } from "./calculateUnits";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

export const GET_CALAIM_NOTES = gql`
  query GetCalaimNotes($startDate: Date, $endDate: Date) {
    calaimNotes(startDate: $startDate, endDate: $endDate) {
      id
      participant
      firstName
      lastName
      cid
      cin
      pos_code
      pos_name
      procedurecode
      proceduremodifier
      proceduremod1
      proceduremod2
      proceduremod3
      proceduretype
      procedurelength
      note_entry_date
      procedurenote
      entrydate
      entryby
    }
  }
`;

const getProcedureCodeDisplay = (note: any) => {
  const modifiers = [
    note.proceduremodifier,
    note.proceduremod1,
    note.proceduremod2,
    note.proceduremod3,
  ]
    .filter(Boolean)
    .join(" ");
  return `${note.procedurecode}${modifiers ? ` ${modifiers}` : ""}`;
};

const COLORS = ["#8884d8", "#82ca9d", "#8dd1e1", "#a4de6c", "#d0ed57"];
const DEFAULT_UNIT_BUCKETS = Array.from({ length: 16 }, (_, index) => index + 1);

const createEmptyUnitCounts = () =>
  DEFAULT_UNIT_BUCKETS.reduce((counts, unit) => {
    counts[unit] = 0;
    return counts;
  }, {} as Record<number, number>);

export const DateRangeReport = () => {
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [fetchNotes, { data }] = useLazyQuery(GET_CALAIM_NOTES);
  const [openModal, setOpenModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [notesToDisplay, setNotesToDisplay] = useState({}) as any;

  // Handlers
  const handleSearch = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    fetchNotes({ variables: { startDate, endDate } });
  };
  const handleReset = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };

  // Process notes
  const processedNotes = useMemo(() => {
    if (!data?.calaimNotes) return [];

    const grouped: Record<string, any[]> = {};
    data.calaimNotes.forEach((note: any) => {
      const day = new Date(note.note_entry_date).toLocaleDateString();
      const key = `${note.participant}-${day}-${note.procedurecode}`;
      (grouped[key] ||= []).push(note);
    });

    return Object.values(grouped)
      .flatMap((group) => {
        const totalTime = group.reduce((sum, n) => sum + +n.procedurelength, 0);
        const units = calculateUnits(totalTime);
        group.sort((a, b) => +b.procedurelength - +a.procedurelength);
        return group.map((note, i) => ({
          ...note,
          isEnabled: i === 0,
          displayUnits: i === 0 ? units : 0,
        }));
      })
      .sort((a, b) => new Date(a.note_entry_date).getTime() - new Date(b.note_entry_date).getTime());
  }, [data?.calaimNotes]);

  const unitBuckets = useMemo(() => {
    const buckets = new Set<number>(DEFAULT_UNIT_BUCKETS);

    processedNotes.forEach((note: any) => {
      if (!note.isEnabled) {
        return;
      }

      buckets.add(note.displayUnits);
    });

    return Array.from(buckets).sort((a, b) => a - b);
  }, [processedNotes]);

  // Build distribution
  const unitDistributionByMonth = useMemo(() => {
    const dist: Record<string, { counts: Record<number, number>; percentages: Record<number, string>; total: number }> = {};
    processedNotes.forEach((note: any) => {
      if (!note.isEnabled) return;
      const d = new Date(note.note_entry_date);
      const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      dist[key] ||= { counts: createEmptyUnitCounts(), percentages: {}, total: 0 };
      if (typeof dist[key].counts[note.displayUnits] !== "number") {
        dist[key].counts[note.displayUnits] = 0;
      }
      dist[key].counts[note.displayUnits]++;
      dist[key].total++;
    });
    Object.entries(dist).forEach(([mk, { counts, total }]) => {
      Object.entries(counts).forEach(([u, cnt]) => {
        dist[mk].percentages[+u] = total ? ((cnt / total) * 100).toFixed(2) : "0.00";
      });
    });
    return dist;
  }, [processedNotes]);

  // Raw notes
  const notesByMonth = useMemo(
    () =>
      processedNotes.reduce((acc, note) => {
        const d = new Date(note.note_entry_date);
        const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
        (acc[key] ||= []).push(note);
        return acc;
      }, {} as Record<string, any[]>),
    [processedNotes]
  );

  const openMonthModal = (key: string) => { setSelectedMonth(key); setOpenModal(true); };
  const [yr, mo] = selectedMonth.split("-").map(Number);
  const monthName = yr && mo ? new Date(yr, mo - 1, 1).toLocaleString("default", { month: "long" }) : "";

  // Excel downloads
  const handleExcelDownload = () => {
    const wb = XLSX.utils.book_new();
    const distributionData = Object.entries(unitDistributionByMonth).flatMap(
      ([monthKey, { counts, percentages }]) => {
        const [year, month] = monthKey.split("-").map(Number);
        const monName = new Date(year, month - 1, 1).toLocaleString("default", { month: "long" });
        return Object.entries(counts).map(([units, count]) => ({
          Month: `${monName} ${year}`,
          Units: Number(units),
          Count: count,
          Percentage: percentages[Number(units)],
        }));
      }
    );
    const ws = XLSX.utils.json_to_sheet(distributionData);
    XLSX.utils.book_append_sheet(wb, ws, "Units Distribution");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `billing-report-${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const handleModalExcelDownload = () => {
    const rows = (notesByMonth[selectedMonth] || []).map((note: any) => ({
      Date: new Date(note.note_entry_date).toLocaleDateString(),
      Participant: note.participant,
      Name: `${note.firstName} ${note.lastName}`,
      CID: note.cid,
      CIN: note.cin,
      Procedure: getProcedureCodeDisplay(note),
      Length: note.procedurelength,
      Units: note.displayUnits,
      "Entry Date": new Date(note.entrydate).toLocaleDateString(),
      "Entry By": note.entryby,
      Note: note.procedurenote,
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, `${monthName}_${yr}`);
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `raw-data-${selectedMonth}.xlsx`
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-end">
        <DatePicker date={startDate} setDate={setStartDate} label="Start Date" />
        <DatePicker date={endDate} setDate={setEndDate} label="End Date" />
        <Button onClick={handleSearch} className="flex items-center gap-2"><SearchIcon size={16}/>Search</Button>
        <Button onClick={handleReset} variant="outline">Reset</Button>
        {data && <Button onClick={handleExcelDownload} variant="outline">Download Excel</Button>}
      </div>

      {!data ? (
        <div className="p-4 text-center text-muted-foreground text-sm">Set date range and click Search to view report</div>
      ) : (
        Object.entries(unitDistributionByMonth).map(([key, { counts, percentages, total }]) => {
          const [y, m] = key.split("-").map(Number);
          const name = new Date(y, m - 1, 1).toLocaleString("default", { month: "long" });
          const pieData = unitBuckets.map((unit) => ({
            name: `${unit} unit${unit !== 1 ? "s" : ""}`,
            value: counts[unit] || 0,
          }));
          return (
            <div key={key} className="mt-6 flex items-start gap-6">
              <div className="flex-1">
                <h3 className="text-md font-medium cursor-pointer text-blue-600 hover:underline" onClick={() => openMonthModal(key)}>{name} {y} (Billing Events: {total})</h3>
                <Table className="text-sm">
                  <TableHeader><TableRow><TableHead>Units</TableHead><TableHead>Count</TableHead><TableHead>% of Month</TableHead></TableRow></TableHeader>
                  <TableBody>{unitBuckets.map(unit => <TableRow key={unit}><TableCell>{unit}</TableCell><TableCell>{counts[unit] || 0}</TableCell><TableCell>{percentages[unit] || "0.00"}%</TableCell></TableRow>)}</TableBody>
                </Table>
              </div>
              <div className="w-40 h-40">
                <ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>{pieData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}</Pie><Tooltip/></PieChart></ResponsiveContainer>
              </div>
            </div>
          );
        })
      )}

      <Dialog
        open={openModal}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setOpenModal(false);
          }
        }}
      >
        <DialogContent
          className="bg-white rounded-lg p-6 w-11/12 h-[90vh] mx-auto mt-10 overflow-auto max-w-none"
          overlayClassName="bg-black/50"
          onInteractOutside={(event) => event.preventDefault()}
        >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">Raw Data - {monthName} {yr}</h2>
          <Button onClick={handleModalExcelDownload} variant="outline">Download Excel</Button>
        </div>
        <div className="overflow-auto h-[80vh]">
          <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Participant</TableHead><TableHead>Name</TableHead><TableHead>CID</TableHead><TableHead>CIN</TableHead><TableHead>Procedure</TableHead><TableHead>Length</TableHead><TableHead>Units</TableHead><TableHead>Entry Date</TableHead><TableHead>Entry By</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(notesByMonth[selectedMonth]||[]).map((note:any)=><React.Fragment key={note.id}>
                <TableRow className={!note.isEnabled?"bg-gray-100":""}>
                  <TableCell>{new Date(note.note_entry_date).toLocaleDateString()}</TableCell><TableCell>{note.participant}</TableCell><TableCell>{note.firstName} {note.lastName}</TableCell><TableCell>{note.cid}</TableCell><TableCell>{note.cin}</TableCell><TableCell>{getProcedureCodeDisplay(note)}</TableCell><TableCell>{note.procedurelength}</TableCell><TableCell>{note.displayUnits}</TableCell><TableCell>{new Date(note.entrydate).toLocaleDateString()}</TableCell><TableCell>{note.entryby}</TableCell><TableCell>{notesToDisplay[note.id]?<ChevronUp onClick={()=>setNotesToDisplay({...notesToDisplay,[note.id]:!notesToDisplay[note.id]})}/>:<ChevronDown onClick={()=>setNotesToDisplay({...notesToDisplay,[note.id]:!notesToDisplay[note.id]})}/>}</TableCell>
                </TableRow>
                {notesToDisplay[note.id]&&<TableRow><TableCell colSpan={11}>{note.procedurenote}</TableCell></TableRow>}
              </React.Fragment>)}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 text-right"><Button onClick={() => setOpenModal(false)}>Close</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
